import { useCallback, useState } from "react";

export type Lingering = {
  /** The group you just left, still drawn open; null once it has closed. */
  lingeringKey: string | null;
  /** Spread on the bar: the pointer being over it is what defers the close. */
  barProps: { onPointerEnter: () => void; onPointerLeave: () => void };
  /** Whether the pointer is on the bar. */
  over: boolean;
  /** Close the lingering group now (it stopped fitting). */
  settle: () => void;
};

/**
 * Keeps the group you just left open until the pointer leaves the bar, so a
 * second click lands where you aimed (Chrome does the same with tab widths
 * after a close). Only a switch made with the pointer on the bar lingers; one
 * from the sidebar, a link, the keyboard or a notification closes it at once.
 */
export function useLingeringGroup(
  openKey: string | null,
  groupKeys: string[]
): Lingering {
  const [over, setOver] = useState(false);
  const [state, setState] = useState<{
    open: string | null;
    lingering: string | null;
  }>({ open: openKey, lingering: null });

  if (openKey !== state.open) {
    setState({
      open: openKey,
      lingering: over && state.open ? state.open : null
    });
  }

  const settle = useCallback(
    () => setState((s) => (s.lingering ? { ...s, lingering: null } : s)),
    []
  );
  const onPointerEnter = useCallback(() => setOver(true), []);
  const onPointerLeave = useCallback(() => {
    setOver(false);
    settle();
  }, [settle]);

  const lingeringKey =
    state.lingering &&
    state.lingering !== openKey &&
    groupKeys.includes(state.lingering)
      ? state.lingering
      : null;
  return {
    lingeringKey,
    barProps: { onPointerEnter, onPointerLeave },
    over,
    settle
  };
}
